/**
 * Client-side password gate. Not real security — anyone who can view the
 * JS bundle can extract the hash and brute-force short passwords — but it
 * keeps casual visitors out of a shared URL. Stack proper auth
 * (Cloudflare Access, or a reverse-proxy Basic Auth) on top for anything
 * that actually matters.
 *
 * The gate is enabled at build time via VITE_PIRATES_PASSWORD_HASH — a
 * SHA-256 hex digest of the shared password. When the env var is empty
 * the gate is bypassed (local dev).
 */

import { useCallback, useEffect, useState, type ReactNode } from "react";

const STORAGE_KEY = "pirates.auth.v1";

async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export interface PasswordGateProps {
  expectedHash: string | undefined;
  children: ReactNode;
}

export function PasswordGate({ expectedHash, children }: PasswordGateProps) {
  const [unlocked, setUnlocked] = useState<boolean>(
    () => !expectedHash || sessionStorage.getItem(STORAGE_KEY) === expectedHash,
  );
  const [entry, setEntry] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!expectedHash) setUnlocked(true);
  }, [expectedHash]);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!expectedHash) return;
      setError(null);
      setChecking(true);
      try {
        const hex = await sha256Hex(entry);
        if (hex === expectedHash) {
          sessionStorage.setItem(STORAGE_KEY, expectedHash);
          setUnlocked(true);
        } else {
          setError("Password didn't match.");
        }
      } finally {
        setChecking(false);
      }
    },
    [entry, expectedHash],
  );

  if (unlocked) return <>{children}</>;

  return (
    <div className="gate">
      <form className="gate__card" onSubmit={submit}>
        <h1 className="gate__title">🏴‍☠️ Buried Treasure</h1>
        <p className="gate__hint">Password required.</p>
        <input
          type="password"
          autoFocus
          value={entry}
          onChange={(e) => setEntry(e.target.value)}
          placeholder="password"
          className="gate__input"
        />
        <button type="submit" disabled={checking || entry.length === 0}>
          {checking ? "Checking…" : "Unlock"}
        </button>
        {error && <div className="gate__error">{error}</div>}
      </form>
    </div>
  );
}
